// src/oura/components/client/ReportsCard.tsx
//
// The reports this client has been sent, on the staff side (#4009).
//
// Staff could upload a report from this tab and then had no way to see the
// result: the only sign of it was a toast. Someone unsure whether an upload
// worked had to sign in as the client, or upload it again.
//
// The list is the same one the merchant's Reports tab shows -- the client
// page's `reports`, which merges the engagement's documents, every completed
// assessment's rendered report and the staff-uploaded PDFs -- read by business
// rather than by login. One source, so the two sides cannot disagree about
// what a client has.
//
// A row opens the file in a new tab. It is a real link, not a button that
// calls `window.open`, so it can be copied and middle-clicked, and the row
// itself is not a second control around it.

import { useCallback, useEffect, useRef, useState } from "react";
import { FileCheck2Icon, FileTextIcon } from "lucide-react";
import { isInAppSnapshot, listBusinessReports, type ClientReportLink } from "@service/aeo/clientReview";
import { byNewest, metaText } from "./reportRows";
import { cn } from "../../lib/cn";
import { ArrowLink, FoldCard, INLINE_PILL, IconSquare, ListRow, TYPE, ROW_STRETCH } from "@oura/ui";

type State =
    | { kind: "loading" }
    | { kind: "failed" }
    | { kind: "ready"; reports: ClientReportLink[] };

export function ReportsCard({ businessId, reloadKey = 0 }: { businessId: string; reloadKey?: number }) {
    const [state, setState] = useState<State>({ kind: "loading" });
    /**
     * Only the newest read may paint.
     *
     * Three things ask for this list -- mounting, an upload, and the tab coming
     * back -- and they are not ordered. An upload's read landing before an
     * older one that was already out would be overwritten by that older
     * answer: the card would drop the report just uploaded, which is the
     * uncertainty this card exists to remove (Jack, #4014).
     */
    const seq = useRef(0);

    const read = useCallback(async () => {
        const mine = ++seq.current;
        const { reports, failed } = await listBusinessReports(businessId);
        if (mine !== seq.current) return;
        // A read that did not land is not an empty list: a client with no
        // reports and a failed read must not look the same.
        setState(failed ? { kind: "failed" } : { kind: "ready", reports: [...reports].sort(byNewest) });
    }, [businessId]);

    const readSafely = useCallback(() => {
        const mine = seq.current + 1;
        void read().catch(() => {
            if (mine === seq.current) setState({ kind: "failed" });
        });
    }, [read]);

    useEffect(() => {
        setState({ kind: "loading" });
        readSafely();
        // Anything still out belongs to the business or upload being left
        // behind, so it must not paint over what comes next.
        return () => { seq.current += 1; };
    }, [readSafely, reloadKey]);

    // An upload on this tab refreshes through `reloadKey`; coming back to the
    // tab reconciles once. NOT `useVisiblePoll`, which also ticks on a timer
    // while visible: this read is the whole client page, and a timer would
    // cost one full page read a minute per open staff tab.
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === "visible") readSafely();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [readSafely]);

    const count = state.kind === "ready" ? state.reports.length : null;

    return (
        <FoldCard
            id="reports"
            title="Reports"
            icon={{ icon: FileTextIcon, area: "setup" }}
            right={count ? String(count) : undefined}
            foldedFact={count === 0 ? "None yet" : undefined}
        >
            {state.kind === "loading" && <p className={cn(TYPE.tb, "m-0 text-gray-500")}>Reading the client’s reports…</p>}
            {state.kind === "failed" && (
                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>
                    Could not read this client’s reports. It will try again when this tab comes back to the front.
                </p>
            )}
            {state.kind === "ready" && state.reports.length === 0 && (
                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>No report has been sent to this client yet.</p>
            )}
            {state.kind === "ready" && state.reports.length > 0 && (
                <div>
                    {state.reports.map((r) => (
                        <ReportRow key={r.id} report={r} />
                    ))}
                </div>
            )}
        </FoldCard>
    );
}

/**
 * One report: its file icon, title, facts and Open. Also listed on the staff
 * Documents page, which passes `onOpen` so the row opens the report in place
 * (the PDF viewer, or the Baseline Snapshot drawn in-app) instead of a new tab.
 */
export function ReportRow({ report, onOpen }: { report: ClientReportLink; onOpen?: (report: ClientReportLink) => void }) {
    const file = Boolean(report.url);
    const approved = report.kind === "doc_approved";
    const inPlace = Boolean(onOpen) && (file || isInAppSnapshot(report));
    if (inPlace && onOpen) {
        return (
            <ListRow
                icon={<IconSquare tone={approved ? "green" : "red"} icon={approved ? FileCheck2Icon : FileTextIcon} />}
                title={report.title}
                sub={metaText(report)}
                // "View" in place of the chevron, as on the merchant's Reports; the whole row opens it.
                stretch
                right={
                    <ArrowLink stretch onClick={() => onOpen(report)} ariaLabel={`View ${report.title}`}>
                        View
                    </ArrowLink>
                }
                className="py-3"
            />
        );
    }
    return (
        <ListRow
            // Red for a PDF, green for a document the client approved, grey
            // for a row with no file to open.
            icon={<IconSquare tone={!file ? "gray" : approved ? "green" : "red"} icon={approved ? FileCheck2Icon : FileTextIcon} />}
            title={report.title}
            sub={metaText(report)}
            muted={!file}
            stretch={file}
            right={
                report.url ? (
                    <a
                        href={report.url}
                        target="_blank"
                        rel="noreferrer"
                        className={cn(INLINE_PILL.base, INLINE_PILL.secondary, INLINE_PILL.row, ROW_STRETCH)}
                    >
                        Open
                    </a>
                ) : undefined
            }
            className="py-3"
        />
    );
}
